import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useMemo } from 'react';
import { 
  Workspace, 
  WorkspaceMember, 
  Bud, 
  WorkspaceId, 
  BudId
} from '@/lib/types';

interface WorkspaceStore {
  // State
  workspaces: Record<WorkspaceId, Workspace>
  members: Record<WorkspaceId, WorkspaceMember[]>
  buds: Record<WorkspaceId, Bud[]>
  
  // Loading states
  loading: {
    workspaces: boolean
    buds: Record<WorkspaceId, boolean>
  }
  
  
  // Actions - Workspace Management
  setWorkspaces: (workspaces: Workspace[]) => void
  addWorkspace: (workspace: Workspace) => void
  updateWorkspace: (id: WorkspaceId, updates: Partial<Workspace>) => void
  removeWorkspace: (id: WorkspaceId) => void
  
  // Actions - Members Management
  setWorkspaceMembers: (workspaceId: WorkspaceId, members: WorkspaceMember[]) => void
  addWorkspaceMember: (workspaceId: WorkspaceId, member: WorkspaceMember) => void
  removeWorkspaceMember: (workspaceId: WorkspaceId, userId: string) => void
  updateMemberRole: (workspaceId: WorkspaceId, userId: string, role: string) => void
  
  
  // Actions - Buds Management
  setBuds: (workspaceId: WorkspaceId, buds: Bud[]) => void
  addBud: (bud: Bud) => void
  updateBud: (id: BudId, updates: Partial<Bud>) => void
  removeBud: (id: BudId) => void
  
  // Actions - Loading States
  setWorkspacesLoading: (loading: boolean) => void
  setBudsLoading: (workspaceId: WorkspaceId, loading: boolean) => void
  
  // Actions - Persistence
  hydrate: (data: Partial<Pick<WorkspaceStore, 'workspaces' | 'buds'>>) => void
}

export const useWorkspaceStore = create<WorkspaceStore>()(
  subscribeWithSelector(
    persist(
      immer((set) => ({
        // Initial state
        workspaces: {},
        members: {},
        buds: {},
        loading: {
          workspaces: false,
          buds: {},
        },
        
        // Workspace Management Actions
        setWorkspaces: (workspaces) => set((state) => {
          state.workspaces = Object.fromEntries(workspaces.map(w => [w.id, w]));
        }),
        
        addWorkspace: (workspace) => set((state) => {
          state.workspaces[workspace.id as WorkspaceId] = workspace;
        }),
        
        updateWorkspace: (id, updates) => set((state) => {
          if (state.workspaces[id]) {
            Object.assign(state.workspaces[id], updates);
          }
        }),
        
        removeWorkspace: (id) => set((state) => {
          delete state.workspaces[id];
          delete state.members[id];
          delete state.buds[id];
          delete state.loading.buds[id];
        }),
        
        // Members Management Actions
        setWorkspaceMembers: (workspaceId, members) => set((state) => {
          state.members[workspaceId] = members;
        }),
        
        addWorkspaceMember: (workspaceId, member) => set((state) => {
          if (!state.members[workspaceId]) {
            state.members[workspaceId] = [];
          }
          const existing = state.members[workspaceId].findIndex(m => m.user_id === member.user_id);
          if (existing >= 0) {
            state.members[workspaceId][existing] = member;
          } else {
            state.members[workspaceId].push(member);
          }
        }),
        
        removeWorkspaceMember: (workspaceId, userId) => set((state) => {
          if (state.members[workspaceId]) {
            state.members[workspaceId] = state.members[workspaceId].filter(m => m.user_id !== userId);
          }
        }),
        
        updateMemberRole: (workspaceId, userId, role) => set((state) => {
          const members = state.members[workspaceId];
          if (members) {
            const member = members.find(m => m.user_id === userId);
            if (member) {
              member.role = role;
            }
          }
        }),
        
        
        // Buds Management Actions
        setBuds: (workspaceId, buds) => set((state) => {
          state.buds[workspaceId] = buds.sort((a, b) => a.name.localeCompare(b.name));
        }),
        
        addBud: (bud) => set((state) => {
          const workspaceId = bud.workspace_id;
          if (!workspaceId) return; // Personal buds not supported yet
          
          if (!state.buds[workspaceId as WorkspaceId]) {
            state.buds[workspaceId as WorkspaceId] = [];
          }
          
          state.buds[workspaceId as WorkspaceId].push(bud);
          state.buds[workspaceId as WorkspaceId].sort((a: Bud, b: Bud) => a.name.localeCompare(b.name));
        }),
        
        updateBud: (id, updates) => set((state) => {
          // Find bud across all workspaces
          for (const workspaceId in state.buds) {
            const buds = state.buds[workspaceId as WorkspaceId];
            const index = buds.findIndex((b: Bud) => b.id === id);
            if (index >= 0) {
              Object.assign(buds[index], updates);
              // Re-sort if name changed
              if (updates.name) {
                buds.sort((a: Bud, b: Bud) => a.name.localeCompare(b.name));
              }
              break;
            }
          }
        }),
        
        removeBud: (id) => set((state) => {
          // Remove bud from all workspaces
          for (const workspaceId in state.buds) {
            state.buds[workspaceId as WorkspaceId] = state.buds[workspaceId as WorkspaceId].filter((b: Bud) => b.id !== id);
          }
        }),
        
        // Loading States Actions
        setWorkspacesLoading: (loading) => set((state) => {
          state.loading.workspaces = loading;
        }),
        
        
        setBudsLoading: (workspaceId, loading) => set((state) => {
          state.loading.buds[workspaceId] = loading;
        }),
        
        
        // Persistence Actions
        hydrate: (data) => set((state) => {
          if (data.workspaces) {
            Object.assign(state.workspaces, data.workspaces);
          }
          if (data.buds) {
            Object.assign(state.buds, data.buds);
          }
        }),
      })),
      {
        name: 'workspace-store',
        partialize: (state) => ({
          workspaces: state.workspaces,
          // Persist buds for offline access
          buds: state.buds,
          // Don't persist loading states or members (they change frequently)
        }),
      }
    )
  )
);

// Selectors for performance
export const useWorkspace = (workspaceId: WorkspaceId) =>
  useWorkspaceStore((state) => state.workspaces[workspaceId]);

export const useWorkspaces = () => {
  const workspaces = useWorkspaceStore((state) => state.workspaces);
  return useMemo(() => Object.values(workspaces), [workspaces]);
};


export const useWorkspaceBuds = (workspaceId: WorkspaceId) => {
  const buds = useWorkspaceStore((state) => state.buds[workspaceId]);
  return useMemo(() => buds || [], [buds]);
};

export const useWorkspaceLoading = (workspaceId: WorkspaceId) =>
  useWorkspaceStore((state) => 
    state.loading.workspaces || 
    state.loading.buds[workspaceId] || 
    false
  );

// Individual action hooks - the proper Zustand way
export const useSetWorkspaces = () => useWorkspaceStore((state) => state.setWorkspaces);
export const useSetWorkspacesLoading = () => useWorkspaceStore((state) => state.setWorkspacesLoading);